import type { LucideIcon } from 'lucide-react';
import { Megaphone, Pencil, QrCode, ScanLine, Shield, Users } from 'lucide-react';

export interface EventNavContext {
  communityId: string;
  eventId: string;
}

export interface EventManagementOption {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  getHref: (ctx: EventNavContext) => string;
  color: string;
  bgColor: string;
}

export function getEventManagementOptions(): EventManagementOption[] {
  return [
    {
      key: 'registrations',
      title: 'Registrations',
      description: 'View and manage all attendees, export registration data.',
      icon: Users,
      getHref: ({ communityId, eventId }) => `/${communityId}/events/${eventId}/registrations`,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      key: 'admins',
      title: 'Event Admins',
      description: 'Assign and manage staff who have access to this event.',
      icon: Shield,
      getHref: ({ communityId, eventId }) => `/${communityId}/events/${eventId}/admins`,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      key: 'activities',
      title: 'Activities & Scanner',
      description: 'Manage event check-ins, activities, and walk-in settings.',
      icon: QrCode,
      getHref: ({ communityId, eventId }) => `/${communityId}/events/${eventId}/activities`,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      key: 'marketing',
      title: 'Marketing',
      description: 'Send event info, reminders, and event updates on WhatsApp.',
      icon: Megaphone,
      getHref: ({ communityId, eventId }) => `/${communityId}/events/${eventId}/marketing`,
      color: 'text-rose-500',
      bgColor: 'bg-rose-500/10',
    },
    {
      key: 'edit',
      title: 'Edit Details',
      description: 'Modify event information, timing, location, and banners.',
      icon: Pencil,
      getHref: ({ communityId, eventId }) => `/${communityId}/events/${eventId}/edit`,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
    },
  ];
}

export function getEventManagementBadge(
  key: string,
  registrationCount?: number
): string | undefined {
  if (key === 'registrations' && typeof registrationCount === 'number') {
    return `${registrationCount} Registrations`;
  }
  return undefined;
}

export function getEventActivitiesPath(ctx: EventNavContext) {
  return `/${ctx.communityId}/events/${ctx.eventId}/activities`;
}

export function getEventScannerPath(ctx: EventNavContext, activities?: { id: string }[] | null) {
  const activitiesPath = getEventActivitiesPath(ctx);
  if (activities?.length === 1) {
    return `${activitiesPath}/${activities[0].id}/scanner`;
  }
  return activitiesPath;
}

export const eventScannerQuickAction = {
  key: 'scanner',
  title: 'Open Scanner',
  shortLabel: 'Scanner',
  icon: ScanLine,
};
